import React, { useState } from "react";
import { Ban, CheckCircle2, Flag, Loader2, MessageSquare, ShieldOff } from "lucide-react";
import UserBanModal from "../../users/modals/UserBanModal";
import { authorInitials, channelLabel, formatReportWhen } from "../achQueueHelpers";

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function bodyTypeLabel(type) {
  if (type === "poll") return "Poll";
  if (type === "bond_wheel") return "Bond wheel";
  return "Text message";
}

export default function ReportedMessageDetail({
  item,
  banLoading,
  dismissLoading,
  onBan,
  onDismiss,
}) {
  const [banOpen, setBanOpen] = useState(false);

  if (!item) {
    return (
      <section className="amf-detail ach-detail ach-detail-idle" aria-label="Select a report">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Chat moderation</p>
          <p className="amf-idle-title">Select a reported message</p>
          <p className="amf-idle-copy">
            Review the flagged content and reporter note. Ban the sender or dismiss the report.
          </p>
        </div>
      </section>
    );
  }

  const author = item.author || {};
  const isBanned = Boolean(author.isBanned);
  const initials = authorInitials(author.name);
  const reason = String(item.reportReason || "").trim();
  const messageText = item.body?.text || "—";
  const typeLabel = bodyTypeLabel(item.body?.type);

  const handleBanConfirm = (userId, banReason) => {
    onBan(userId, banReason);
    setBanOpen(false);
  };

  return (
    <section className="amf-detail ach-detail" aria-label="Report detail">
      <header className="ach-report-header">
        <div className="ach-report-sender">
          <span className="ach-report-avatar" aria-hidden>
            {initials}
          </span>
          <div className="ach-report-sender-copy">
            <div className="ach-report-sender-top">
              <h2 className="ach-report-name">{author.name || "Unknown sender"}</h2>
              <span className={`ach-report-channel ach-report-channel-${item.channel}`}>
                {channelLabel(item.channel)}
              </span>
            </div>
            <p className="ach-report-email">{author.email || "No email on file"}</p>
          </div>
        </div>

        <div className="ach-report-actions">
          <button
            type="button"
            className="ach-report-act"
            disabled={dismissLoading}
            onClick={() => onDismiss(item)}
          >
            {dismissLoading ? (
              <Loader2 size={12} className="ach-spin" aria-hidden />
            ) : (
              <CheckCircle2 size={12} aria-hidden />
            )}
            <span>Dismiss</span>
          </button>
          <button
            type="button"
            className={`ach-report-act ${isBanned ? "ach-report-act-unban" : "ach-report-act-ban"}`}
            disabled={!author._id || banLoading}
            onClick={() => setBanOpen(true)}
          >
            {isBanned ? <ShieldOff size={12} aria-hidden /> : <Ban size={12} aria-hidden />}
            <span>{isBanned ? "Unban" : "Ban"}</span>
          </button>
        </div>
      </header>

      <div className="amf-detail-scroll ach-detail-scroll">
        {isBanned ? (
          <aside className="ach-report-ban" role="note">
            <strong>Suspended</strong>
            <span>{author.banReason || "No reason recorded"}</span>
          </aside>
        ) : null}

        <article className="ach-report-case" aria-labelledby="ach-report-case-title">
          <header className="ach-report-case-head">
            <MessageSquare size={12} aria-hidden />
            <h3 id="ach-report-case-title" className="ach-report-case-title">
              Flagged content
            </h3>
            {item.fixtureLabel ? (
              <span className="ach-report-fixture" title={item.fixtureLabel}>
                {item.fixtureLabel}
              </span>
            ) : null}
          </header>

          <blockquote className="ach-report-message" cite={`message-${item.messageId}`}>
            <p className="ach-report-message-text">{messageText}</p>
            <footer className="ach-report-message-meta">
              <span>{typeLabel}</span>
              <span aria-hidden>·</span>
              <time dateTime={item.createdAt}>Sent {formatWhen(item.createdAt)}</time>
            </footer>
          </blockquote>

          <div className="ach-report-reason" role="note" aria-label="Reporter explanation">
            <div className="ach-report-reason-head">
              <span className="ach-report-reason-icon" aria-hidden>
                <Flag size={11} strokeWidth={2.4} />
              </span>
              <div className="ach-report-reason-copy">
                <span className="ach-report-reason-label">Reporter flagged because</span>
                <span className="ach-report-reason-hint">Not chat content — moderation note</span>
              </div>
            </div>
            <p className="ach-report-reason-body">
              {reason || "No explanation provided with this report."}
            </p>
            <p className="ach-report-reason-when">
              Report filed {formatReportWhen(item.reportedAt)}
              {item.reportedAt ? ` · ${formatWhen(item.reportedAt)}` : ""}
            </p>
          </div>
        </article>
      </div>

      <UserBanModal
        open={banOpen}
        user={{
          _id: author._id,
          name: author.name,
          email: author.email,
          isBanned: author.isBanned,
          banReason: author.banReason,
        }}
        loading={banLoading}
        onClose={() => setBanOpen(false)}
        onConfirm={handleBanConfirm}
      />
    </section>
  );
}
